import { useState, useEffect, lazy, Suspense } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { DEFAULT_RELEVANCE_PROMPT } from '../lib/relevanceSettings'
import { supabase } from '../lib/supabase'
import { showError as showToastError } from '../lib/toast'
import { SettingsSidebarTab } from './SettingsSidebarTab'

// Lazy load AIMemoryTab to prevent blocking Settings page load
const AIMemoryTab = lazy(() => import('./AIMemoryTab'))

type SettingsTab = 'date' | 'relevance' | 'ai-memory' | 'sidebar'

function Settings() {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  // Get active tab from URL hash, default to 'date'
  const getActiveTabFromHash = (): SettingsTab => {
    const hash = location.hash.slice(1)
    if (hash === 'relevance' || hash === 'ai-memory' || hash === 'sidebar') {
      return hash as SettingsTab
    }
    return 'date'
  }
  const [activeTab, setActiveTab] = useState<SettingsTab>(getActiveTabFromHash())
  
  // Update active tab when hash changes
  useEffect(() => {
    const newTab = getActiveTabFromHash()
    setActiveTab(newTab)
  }, [location.hash])
  
  const [followUpDays, setFollowUpDays] = useState<number>(7)
  const [deadlineDays, setDeadlineDays] = useState<number>(14)
  
  // Relevance rules settings
  const [relevanceScoreThreshold, setRelevanceScoreThreshold] = useState<number>(75)
  const [relevancePrompt, setRelevancePrompt] = useState<string>(DEFAULT_RELEVANCE_PROMPT)
  const [relevanceMinDescriptionLength, setRelevanceMinDescriptionLength] = useState<number>(50)
  const [relevanceMaxDescriptionLength, setRelevanceMaxDescriptionLength] = useState<number>(3000)

  // Load settings from Supabase
  useEffect(() => {
    // Set timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.warn('[Settings] Loading timeout after 15 seconds')
      setError('Loading timeout. Please refresh the page.')
      setLoading(false)
    }, 15000)

    const loadSettings = async () => {
      try {
        setLoading(true)
        setError(null)

        const { data: { user } } = await supabase.auth.getUser()
        if (!user) {
          setLoading(false)
          navigate('/login')
          return
        }

        // Try to get existing settings
        const { data, error: fetchError } = await supabase
          .from('user_settings')
          .select('follow_up_days_after_applied, deadline_days_after_saved, relevance_score_threshold, relevance_prompt, relevance_min_description_length, relevance_max_description_length')
          .eq('user_id', user.id)
          .maybeSingle()

        if (fetchError) {
          // Ignore errors - use defaults if settings don't exist
          console.warn('Failed to load user settings (using defaults):', fetchError)
        }

        if (data) {
          // Settings exist, load them
          setFollowUpDays(data.follow_up_days_after_applied ?? 7)
          setDeadlineDays(data.deadline_days_after_saved ?? 14)
          setRelevanceScoreThreshold(data.relevance_score_threshold ?? 75)
          setRelevancePrompt(data.relevance_prompt ?? DEFAULT_RELEVANCE_PROMPT)
          setRelevanceMinDescriptionLength(data.relevance_min_description_length ?? 50)
          setRelevanceMaxDescriptionLength(data.relevance_max_description_length ?? 3000)
        } else {
          // Settings don't exist, create them with default values
          // Note: created_at and updated_at will be set automatically by database defaults
          const defaultSettings = {
            user_id: user.id,
            follow_up_days_after_applied: 7,
            deadline_days_after_saved: 14,
            relevance_score_threshold: 75,
            relevance_prompt: 'Analyze this job posting. Return JSON: {"score":0-100,"verdict":"Fit"/"No Fit","reasoning":"..."}.\n\n',
            relevance_min_description_length: 50,
            relevance_max_description_length: 3000,
          }

          const { error: createError } = await supabase
            .from('user_settings')
            .insert(defaultSettings)

          if (createError) {
            console.error('Failed to create default user settings:', createError)
            // Still set defaults in UI even if DB insert fails
          } else {
            console.log('Created default user settings for user:', user.id)
          }

          // Set defaults in state
          setFollowUpDays(defaultSettings.follow_up_days_after_applied)
          setDeadlineDays(defaultSettings.deadline_days_after_saved)
          setRelevanceScoreThreshold(defaultSettings.relevance_score_threshold)
          setRelevancePrompt(defaultSettings.relevance_prompt)
          setRelevanceMinDescriptionLength(defaultSettings.relevance_min_description_length)
          setRelevanceMaxDescriptionLength(defaultSettings.relevance_max_description_length)
        }
      } catch (err: unknown) {
        clearTimeout(loadingTimeout)
        const errorMessage = err instanceof Error ? err.message : String(err)
        const errorMsg = errorMessage || 'Failed to load settings'
        setError(errorMsg)
        showToastError(errorMsg)
      } finally {
        clearTimeout(loadingTimeout)
        setLoading(false)
      }
    }

    loadSettings()
    
    // Set default hash if no hash is present
    if (!location.hash && location.pathname === '/settings') {
      navigate('/settings#date', { replace: true })
    }

    return () => {
      clearTimeout(loadingTimeout)
    }
  }, [navigate, location])

  const handleSaveDateSettings = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(false)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        navigate('/login')
        return
      }

      const settingsToSave = {
        user_id: user.id,
        follow_up_days_after_applied: followUpDays,
        deadline_days_after_saved: deadlineDays,
        updated_at: new Date().toISOString(),
      }

      const { error: upsertError } = await supabase
        .from('user_settings')
        .upsert(settingsToSave, {
          onConflict: 'user_id'
        })
        .select()

      if (upsertError) {
        throw upsertError
      }

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      setError(errorMessage || 'Failed to save date settings')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveRelevanceSettings = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(false)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        navigate('/login')
        return
      }

      const settingsToSave = {
        user_id: user.id,
        relevance_score_threshold: relevanceScoreThreshold,
        relevance_prompt: relevancePrompt,
        relevance_min_description_length: relevanceMinDescriptionLength,
        relevance_max_description_length: relevanceMaxDescriptionLength,
        updated_at: new Date().toISOString(),
      }

      const { error: upsertError } = await supabase
        .from('user_settings')
        .upsert(settingsToSave, {
          onConflict: 'user_id'
        })
        .select()

      if (upsertError) {
        throw upsertError
      }

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      setError(errorMessage || 'Failed to save relevance settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading && activeTab !== 'sidebar') {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-text-secondary">Loading settings...</div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="p-6 pb-0 flex-shrink-0 max-w-[720px]">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mb-4 inline-flex min-h-11 items-center gap-1 text-small text-text-secondary hover:text-text-primary transition-colors"
        >
          <span>&lt;</span>
          <span>Back to Jobs</span>
        </button>

        {error && (
          <div className="mb-4 p-3 bg-danger-subtle border border-danger/20 rounded text-sm text-danger-strong">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-brand-primary-subtle border border-brand-primary/20 rounded text-sm text-brand-primary">
            Settings saved successfully!
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 flex flex-col px-6 pb-6 min-h-0 overflow-hidden max-w-[720px]">
        <div className={activeTab === 'ai-memory' ? 'flex-1 flex flex-col min-h-0' : 'flex-1 flex flex-col min-h-0 overflow-y-auto'}>
          <>
            {/* Date Settings Tab */}
            {activeTab === 'date' && (
              <div className="space-y-6">
                {/* How It Works - moved to top */}
                <div className="p-4 bg-interactive-subtle border border-border-default rounded-lg">
                  <h3 className="text-sm font-medium text-text-primary mb-2">How it works</h3>
                  <ul className="text-xs text-text-secondary space-y-1 list-disc list-inside">
                    <li><strong>Date Settings:</strong> Apply to all jobs when using "Auto" mode for dates. You can override individual dates by setting them to "Manual" mode.</li>
                    <li>All settings are saved per user and apply universally across all platforms.</li>
                  </ul>
                </div>

                <div className="bg-surface-default border border-border-default rounded-lg shadow-sm p-6 space-y-6">
                  <h2 className="text-h3 text-text-primary font-semibold mb-4">Date Settings</h2>
              
                {/* Follow Up Days Setting */}
                <div>
                  <label htmlFor="follow-up-days" className="block text-sm font-medium text-text-secondary mb-2">
                    Follow Up Days After Applied
                  </label>
                  <p className="text-xs text-text-muted mb-3">
                    Automatically set "Follow Up" date this many days after "Date Applied" is set
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      id="follow-up-days"
                      type="number"
                      min="0"
                      max="365"
                      value={followUpDays}
                      onChange={(e) => setFollowUpDays(parseInt(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-transparent"
                    />
                    <span className="text-sm text-text-secondary">days</span>
                  </div>
                </div>

                {/* Deadline Days Setting */}
                <div>
                  <label htmlFor="deadline-days" className="block text-sm font-medium text-text-secondary mb-2">
                    Deadline Days After Saved
                  </label>
                  <p className="text-xs text-text-muted mb-3">
                    Automatically set "Deadline" date this many days after "Date Saved"
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      id="deadline-days"
                      type="number"
                      min="0"
                      max="365"
                      value={deadlineDays}
                      onChange={(e) => setDeadlineDays(parseInt(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-transparent"
                    />
                    <span className="text-sm text-text-secondary">days</span>
                  </div>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-border-default">
                  <button
                    onClick={handleSaveDateSettings}
                    disabled={saving}
                    className={`
                      px-4 py-2 min-h-11 rounded-md text-sm font-medium transition-colors
                      ${saving
                        ? 'bg-surface-muted text-text-muted cursor-not-allowed'
                        : 'bg-brand-primary text-text-inverse hover:bg-brand-primary-hover'
                      }
                    `}
                  >
                    {saving ? 'Saving...' : 'Save Date Settings'}
                  </button>
                </div>
              </div>
            </div>
            )}

            {/* Relevance Analysis Rules Tab */}
            {activeTab === 'relevance' && (
              <div className="space-y-6">
                {/* How It Works - moved to top */}
                <div className="p-4 bg-interactive-subtle border border-border-default rounded-lg">
                  <h3 className="text-sm font-medium text-text-primary mb-2">How it works</h3>
                  <ul className="text-xs text-text-secondary space-y-1 list-disc list-inside">
                    <li><strong>Relevance Rules:</strong> Used by OpenAI Assistant, web app, and browser extension. Changes take effect for new relevance analyses.</li>
                    <li>All settings are saved per user and apply universally across all platforms.</li>
                  </ul>
                </div>

                <div className="bg-surface-default border border-border-default rounded-lg shadow-sm p-6 space-y-6">
                  <h2 className="text-h3 text-text-primary font-semibold mb-4">Relevance Analysis Rules</h2>
                  <p className="text-xs text-text-muted mb-4">
                    Configure how AI determines if a job is relevant to your profile. These rules are used by OpenAI Assistant, the web app, and the browser extension.
                  </p>

                  {/* Score Threshold */}
                  <div>
                    <label htmlFor="relevance-score-threshold" className="block text-sm font-medium text-text-secondary mb-2">
                      Relevance Score Threshold
                    </label>
                    <p className="text-xs text-text-muted mb-3">
                      Minimum score (0-100) for a job to be considered relevant. Jobs with scores below this threshold will be marked as "No Fit".
                    </p>
                    <div className="flex items-center gap-4">
                      <input
                        id="relevance-score-threshold"
                        type="number"
                        min="0"
                        max="100"
                        value={relevanceScoreThreshold}
                        onChange={(e) => setRelevanceScoreThreshold(parseInt(e.target.value) || 0)}
                        className="w-24 px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-transparent"
                      />
                      <span className="text-sm text-text-secondary">points (0-100)</span>
                    </div>
                  </div>

                  {/* Minimum Description Length */}
                  <div>
                    <label htmlFor="min-description-length" className="block text-sm font-medium text-text-secondary mb-2">
                      Minimum Description Length
                    </label>
                    <p className="text-xs text-text-muted mb-3">
                      Minimum number of characters required in job description for relevance analysis. Jobs with shorter descriptions will be skipped.
                    </p>
                    <div className="flex items-center gap-4">
                      <input
                        id="min-description-length"
                        type="number"
                        min="0"
                        max="10000"
                        value={relevanceMinDescriptionLength}
                        onChange={(e) => setRelevanceMinDescriptionLength(parseInt(e.target.value) || 0)}
                        className="w-24 px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-transparent"
                      />
                      <span className="text-sm text-text-secondary">characters</span>
                    </div>
                  </div>

                  {/* Maximum Description Length */}
                  <div>
                    <label htmlFor="max-description-length" className="block text-sm font-medium text-text-secondary mb-2">
                      Maximum Description Length
                    </label>
                    <p className="text-xs text-text-muted mb-3">
                      Maximum number of characters from job description to send to OpenAI. Longer descriptions will be truncated.
                    </p>
                    <div className="flex items-center gap-4">
                      <input
                        id="max-description-length"
                        type="number"
                        min="100"
                        max="100000"
                        value={relevanceMaxDescriptionLength}
                        onChange={(e) => setRelevanceMaxDescriptionLength(parseInt(e.target.value) || 0)}
                        className="w-24 px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-transparent"
                      />
                      <span className="text-sm text-text-secondary">characters</span>
                    </div>
                  </div>

                  {/* Prompt Template */}
                  <div>
                    <label htmlFor="ai-prompt-template" className="block text-sm font-medium text-text-secondary mb-2">
                      AI Prompt Template
                    </label>
                    <p className="text-xs text-text-muted mb-3">
                      Custom prompt for OpenAI Assistant. Use placeholders: <code className="bg-surface-subtle px-1 rounded">{"{company}"}</code>, <code className="bg-surface-subtle px-1 rounded">{"{title}"}</code>, <code className="bg-surface-subtle px-1 rounded">{"{location}"}</code>, <code className="bg-surface-subtle px-1 rounded">{"{description}"}</code>
                    </p>
                    <textarea
                      id="ai-prompt-template"
                      value={relevancePrompt}
                      onChange={(e) => setRelevancePrompt(e.target.value)}
                      rows={6}
                      className="w-full px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-transparent font-mono text-sm"
                      placeholder="Enter your custom prompt template..."
                    />
                    <p className="text-xs text-text-muted mt-2">
                      Default: {DEFAULT_RELEVANCE_PROMPT}
                    </p>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4 border-t border-border-default">
                    <button
                      onClick={handleSaveRelevanceSettings}
                      disabled={saving}
                      className={`
                        px-4 py-2 min-h-11 rounded-md text-sm font-medium transition-colors
                        ${saving
                          ? 'bg-surface-muted text-text-muted cursor-not-allowed'
                          : 'bg-brand-primary text-text-inverse hover:bg-brand-primary-hover'
                        }
                      `}
                    >
                      {saving ? 'Saving...' : 'Save Relevance Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* AI Memory Tab */}
            {activeTab === 'ai-memory' && (
              <Suspense fallback={
                <div className="bg-surface-default border border-border-default rounded-lg p-6">
                  <div className="flex items-center justify-center py-8">
                    <div className="text-text-secondary">Loading AI Memory...</div>
                  </div>
                </div>
              }>
                <AIMemoryTab />
              </Suspense>
            )}

            {activeTab === 'sidebar' && <SettingsSidebarTab />}
          </>
        </div>
      </div>
    </div>
  )
}

export default Settings
